import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, Loader2, Image as ImageIcon, X } from 'lucide-react';

type SlideForm = {
  title: string;
  subtitle: string;
  tag: string;
  link: string;
  bg_gradient: string;
  is_active: boolean;
};

const gradients = [
  { label: 'Blue', value: 'from-blue-950 via-slate-900 to-blue-950' },
  { label: 'Purple', value: 'from-purple-950 via-slate-900 to-purple-950' },
  { label: 'Emerald', value: 'from-emerald-950 via-slate-900 to-emerald-950' },
  { label: 'Rose', value: 'from-rose-950 via-slate-900 to-rose-950' },
  { label: 'Orange', value: 'from-orange-950 via-slate-900 to-orange-950' },
];

const empty: SlideForm = {
  title: '', subtitle: '', tag: '', link: '/products',
  bg_gradient: 'from-blue-950 via-slate-900 to-blue-950', is_active: true,
};

const AdminPromoSlides = () => {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<SlideForm>(empty);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [existingImage, setExistingImage] = useState('');
  const [uploading, setUploading] = useState(false);

  const { data: slides = [], isLoading } = useQuery({
    queryKey: ['admin', 'promo-slides'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from('promo_slides').select('*').order('sort_order');
      if (error) throw error;
      return data;
    },
  });

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Max 5MB', variant: 'destructive' });
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const saveMutation = useMutation({
    mutationFn: async (f: SlideForm) => {
      let imageUrl = existingImage;
      if (imageFile) {
        setUploading(true);
        const ext = imageFile.name.split('.').pop();
        const path = `promo-slides/slide-${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage.from('homepage').upload(path, imageFile, { cacheControl: '3600', upsert: false });
        if (upErr) throw upErr;
        const { data: { publicUrl } } = supabase.storage.from('homepage').getPublicUrl(path);
        imageUrl = publicUrl;
        setUploading(false);
      }

      const payload = { ...f, image_url: imageUrl, sort_order: editId ? undefined : slides.length };

      if (editId) {
        const { error } = await (supabase as any).from('promo_slides').update(payload).eq('id', editId);
        if (error) throw error;
      } else {
        const { error } = await (supabase as any).from('promo_slides').insert({ ...payload, sort_order: slides.length });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'promo-slides'] });
      qc.invalidateQueries({ queryKey: ['promo-slides'] });
      toast({ title: editId ? 'Slide updated!' : 'Slide created!' });
      reset();
    },
    onError: (e: any) => {
      setUploading(false);
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from('promo_slides').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'promo-slides'] });
      toast({ title: 'Slide deleted' });
    },
  });

  const openEdit = (s: any) => {
    setEditId(s.id);
    setForm({ title: s.title, subtitle: s.subtitle || '', tag: s.tag || '', link: s.link || '/products', bg_gradient: s.bg_gradient, is_active: s.is_active });
    setExistingImage(s.image_url || '');
    setImagePreview(s.image_url || '');
    setImageFile(null);
    setIsOpen(true);
  };

  const reset = () => {
    setIsOpen(false);
    setEditId(null);
    setForm(empty);
    setImageFile(null);
    setImagePreview('');
    setExistingImage('');
  };

  const set = (k: keyof SlideForm, v: any) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold">Promo Slides</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage the promotional banner slider on the homepage</p>
        </div>
        <Dialog open={isOpen} onOpenChange={o => { if (!o) reset(); setIsOpen(o); }}>
          <DialogTrigger asChild>
            <Button className="gap-2"><Plus className="h-4 w-4" /> Add Slide</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editId ? 'Edit' : 'Add'} Promo Slide</DialogTitle></DialogHeader>
            <form onSubmit={e => { e.preventDefault(); saveMutation.mutate(form); }} className="space-y-4">

              {/* Image upload */}
              <div>
                <Label>Background Image (optional)</Label>
                {imagePreview ? (
                  <div className="relative mt-2 group">
                    <img src={imagePreview} alt="" className="w-full h-32 object-cover rounded-lg border" />
                    <button type="button" onClick={() => { setImageFile(null); setImagePreview(''); setExistingImage(''); }}
                      className="absolute top-2 right-2 bg-destructive text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <Label htmlFor="slide-img" className="cursor-pointer mt-2 block">
                    <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-5 text-center hover:border-primary/50 transition-colors">
                      <ImageIcon className="h-7 w-7 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">Click to upload (JPG/PNG, max 5MB)</p>
                    </div>
                  </Label>
                )}
                <input id="slide-img" type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
              </div>

              <div><Label>Title *</Label><Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. vivo T5x 5G" required /></div>
              <div><Label>Subtitle</Label><Input value={form.subtitle} onChange={e => set('subtitle', e.target.value)} placeholder="e.g. New Arrival — Now Available" /></div>
              <div><Label>Tag / Badge</Label><Input value={form.tag} onChange={e => set('tag', e.target.value)} placeholder="e.g. Starting ₹18,999" /></div>
              <div><Label>Link URL</Label><Input value={form.link} onChange={e => set('link', e.target.value)} placeholder="/products?search=vivo" /></div>

              <div>
                <Label>Background Color</Label>
                <Select value={form.bg_gradient} onValueChange={v => set('bg_gradient', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {gradients.map(g => (
                      <SelectItem key={g.value} value={g.value}>
                        <div className="flex items-center gap-2">
                          <div className={`h-4 w-8 rounded bg-gradient-to-r ${g.value}`} />
                          {g.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={v => set('is_active', v)} />
                <Label>Active (visible on homepage)</Label>
              </div>

              <Button type="submit" className="w-full" disabled={saveMutation.isPending || uploading}>
                {saveMutation.isPending || uploading
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{uploading ? 'Uploading...' : 'Saving...'}</>
                  : editId ? 'Update Slide' : 'Create Slide'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : slides.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border-2 border-dashed rounded-xl">
          <p className="mb-2">No promo slides yet</p>
          <p className="text-sm">Click "Add Slide" to create your first promotional banner</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {slides.map((s: any) => (
            <div key={s.id} className={`relative rounded-xl overflow-hidden bg-gradient-to-r ${s.bg_gradient} text-white`} style={{ height: '100px' }}>
              {s.image_url && <img src={s.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
              <div className="relative z-10 flex items-center justify-between h-full px-5">
                <div>
                  {s.tag && <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full mb-1 inline-block">{s.tag}</span>}
                  <p className="font-heading font-bold text-lg">{s.title}</p>
                  {s.subtitle && <p className="text-xs opacity-70">{s.subtitle}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${s.is_active ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                    {s.is_active ? 'Active' : 'Hidden'}
                  </span>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/20" onClick={() => openEdit(s)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-300 hover:bg-red-500/20" onClick={() => deleteMutation.mutate(s.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPromoSlides;
